/**
 * ImagesStep Component - Story 6.3: Create Add Product Form
 * Step 3: Product images form
 */

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { ImageUploadWidget } from '../../ImageUploadWidget'
import { Image as ImageType } from '../../../types/productForm.types'

export interface ImagesStepProps {
  images: ImageType[]
  onAddImages: (files: File[]) => void
  onRemoveImage: (index: number) => void
  isLoading?: boolean
  errors: Record<string, string>
  mode?: 'create' | 'edit'
}

/**
 * ImagesStep Component
 * Form step for uploading product images
 *
 * @component
 * @example
 * <ImagesStep
 *   images={[]}
 *   onAddImages={handleAddImages}
 *   onRemoveImage={handleRemoveImage}
 *   errors={{}}
 * />
 */
export function ImagesStep({
  images,
  onAddImages,
  onRemoveImage,
  isLoading = false,
  errors,
  mode = 'create',
}: ImagesStepProps) {
  // Check if there are existing images (images with id but no file)
  const hasExistingImages = images.some(img => img.id && !img.file);

  return (
    <Card className="images-step">
      <CardHeader>
        <CardTitle>Product Images</CardTitle>
        <CardDescription>
          Upload up to 5 product images (JPEG, PNG, WebP) - Optional
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {errors['images'] && (
          <div
            className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3"
            role="alert"
            id="images-error"
          >
            {errors['images']}
          </div>
        )}

        {/* Guidance for editing products with existing images */}
        {mode === 'edit' && hasExistingImages && (
          <div className="text-sm bg-green-50 border border-green-200 rounded-md p-3 flex gap-2">
            <div className="flex-1">
              <p className="font-semibold text-green-900 mb-1">✅ Auto-Replace Mode</p>
              <ul className="text-green-800 space-y-1 text-xs">
                <li>• When you upload new images, old ones will be <strong>automatically removed</strong></li>
                <li>• Only your new images will be saved</li>
                <li>• Upload as many images as you want (max 5)</li>
              </ul>
            </div>
          </div>
        )}

        <ImageUploadWidget
          images={images}
          onAddImages={onAddImages}
          onRemoveImage={onRemoveImage}
          isLoading={isLoading}
          disabled={isLoading}
        />
      </CardContent>
    </Card>
  )
}
